import {
  RUNTIME_CONFIG_PATH,
  RuntimeConfigError,
  validateRuntimeConfig,
  type RuntimeConfigDocument,
} from '@albuelis-accounting/shared';

/**
 * Runtime configuration loading.
 *
 * The frontend has no compiled-in knowledge of its backend. At startup it fetches a
 * configuration document and everything else — the API base URL, the auth mode, the
 * identity provider's coordinates, the limits — comes from there. That is what makes
 * one build runnable on localhost against a deployed environment and behind
 * CloudFront against the deployed API without a rebuild.
 *
 * `VITE_APP_CONFIG_URL` is the only environment-specific value the build or the dev
 * server reads. Nothing else is read from `import.meta.env` anywhere in the app.
 */

/** Where the document is fetched from. */
export function runtimeConfigUrl(): string {
  const configured = import.meta.env.VITE_APP_CONFIG_URL as string | undefined;
  if (typeof configured === 'string' && configured.trim() !== '') {
    return configured.trim();
  }
  // Behind CloudFront the document is served from the application's own origin at a
  // stable path, so a same-origin relative URL is the correct default and the
  // deployed build needs no environment variable. Locally the dev server serves the
  // file placed in `public/`, so the same path works there too.
  return RUNTIME_CONFIG_PATH;
}

/** Thrown with an operator-facing message when startup configuration is unusable. */
export class ConfigLoadError extends Error {
  constructor(
    message: string,
    readonly source: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ConfigLoadError';
  }
}

let cached: RuntimeConfigDocument | undefined;
let inFlight: Promise<RuntimeConfigDocument> | undefined;

/**
 * Fetches and validates the configuration document, once per session.
 *
 * Deliberately has no fallback. If the document is missing, unreachable, malformed
 * or of an unsupported schema version, this rejects with a message naming the URL and
 * the problem. Guessing an API endpoint instead would turn a clear startup failure
 * into an authentication error much later, somewhere much harder to diagnose.
 *
 * Concurrent callers share one request and the result is cached for the session, so
 * navigation and API calls never re-fetch it.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfigDocument> {
  if (cached) return cached;
  if (inFlight) return inFlight;

  const url = runtimeConfigUrl();

  inFlight = (async () => {
    let response: Response;
    try {
      response = await fetch(url, {
        // The document is deliberately anonymous-readable; sending credentials would
        // add nothing and would force a preflight.
        credentials: 'omit',
        // Always revalidate. A stale copy in the browser cache would survive a
        // configuration change, which is exactly what the deployment's cache headers
        // are set up to avoid.
        cache: 'no-cache',
      });
    } catch (error) {
      throw new ConfigLoadError(
        `The application could not reach its configuration document at ${url}. ` +
          'Locally, run `npm run web:dev -- dev1` (or `-- test1`) from the repo root, which ' +
          'points the dev server at the deployed environment; when deployed, check that ' +
          'the config document exists and that this origin is allowed by its CORS rules.',
        url,
        error,
      );
    }

    if (!response.ok) {
      throw new ConfigLoadError(
        `The configuration document at ${url} returned HTTP ${response.status}. ` +
          (response.status === 403 || response.status === 404
            ? 'Confirm the file is being served and that the URL includes the ' +
              'config/app-config.json key.'
            : 'Retry in a moment; if it persists, check the deployment.'),
        url,
      );
    }

    let parsed: unknown;
    try {
      parsed = await response.json();
    } catch (error) {
      throw new ConfigLoadError(
        `The configuration document at ${url} is not valid JSON. This usually means the URL ` +
          'resolved to something else — an error page or the application shell — rather than ' +
          'the document.',
        url,
        error,
      );
    }

    try {
      const document = validateRuntimeConfig(parsed, url);
      cached = document;
      return document;
    } catch (error) {
      if (error instanceof RuntimeConfigError) {
        throw new ConfigLoadError(`${error.message} (from ${url})`, url, error);
      }
      throw error;
    } finally {
      inFlight = undefined;
    }
  })();

  return inFlight;
}

/**
 * The loaded configuration.
 *
 * Throws if called before loading completed, which is a programming error rather
 * than a runtime condition: the application shell does not render its routes until
 * the document has resolved. Non-React code reads it through here so there is one
 * accessor rather than values threaded through unrelated props.
 */
export function runtimeConfig(): RuntimeConfigDocument {
  if (!cached) {
    throw new Error(
      'Runtime configuration was read before it finished loading. Anything that needs it must ' +
        'render beneath the configuration provider.',
    );
  }
  return cached;
}

/** Test seam: install or clear the cached document. */
export function setRuntimeConfigForTesting(document: RuntimeConfigDocument | undefined): void {
  cached = document;
  inFlight = undefined;
}
